import {
  TripAttraction,
  TripStorageCommand,
  UserTripModel,
} from './../models/vititor.trip.storage.command';
import { isEmptyArray, upperFirstLetter } from 'src/app/common/utils/object.extensions';
import { EventTimeService } from './../../event-calendar/services/event.time.service';
import { VisitorEvent } from './../models/visitor.event';
import { Injectable } from '@angular/core';
import { catchError, lastValueFrom, map, Observable, of, tap } from 'rxjs';
import { ApiRequestOptions, ApiService } from 'src/app/common/services/api/api.service';
import { IFrontendSettings } from '../ngrx.stores/frontend.settings/states';
import { CreateTripCommand, UserTrip } from '../models/vititor.trip.storage.command';
import { VisitorPlace } from '../models/visitor.place';
import { AIModel } from '../models/travel.assistant';
import { JobPosting } from '../models/job.posting';

@Injectable({
  providedIn: 'root',
})
export class VisitorService {
  constructor(
    private apiService: ApiService,
    private timeService: EventTimeService,
  ) {}

  public getFrontendSettings(hubName: string): Observable<IFrontendSettings> {
    return this.apiService.get(`visitors/${hubName}/all-frontend-settings`);
  }

  public getItemType(item: VisitorEvent | VisitorPlace | JobPosting): 'event' | 'place' | 'job' {
    if (item['startTime']) return 'event';
    if (item['company_name']) return 'job';
    return 'place';
  }

  public getImageBase64(imgUrl): Observable<any> {
    const options = new ApiRequestOptions();
    options.timeout = 55 * 1000;
    return this.apiService.get(
      `common/image-base64?url=${encodeURIComponent(imgUrl)}`,
      undefined,
      options,
    );
  }

  public travelBuddyConversation(data) {
    const startTime = Date.now();

    return this.apiService.post(`visitors/${data.hubName}/travel-buddy-conversation`, data).pipe(
      tap((response) => {
        const actualTime = Date.now() - startTime;
        console.log(`Total request time: ${actualTime}ms`);

        // Log backend response time if available
        if (response?.timeConsumed) {
          console.log(`Backend processing time: ${response.timeConsumed}ms`);
          console.log(`Frontend overhead: ${actualTime - response.timeConsumed}ms`);

          // Performance feedback
          if (response.timeConsumed < 2000) {
            console.log('�Fast response achieved!');
          } else if (response.timeConsumed > 5000) {
            console.warn('⚠️ Response slower than expected:', response.timeConsumed + 'ms');
            // Could trigger additional monitoring or user notification here
            this.handleSlowResponse(response.timeConsumed);
          }
        }
      }),
    );
  }

  private handleSlowResponse(responseTime: number) {
    // Log slow response for monitoring
    console.error(`Slow response detected: ${responseTime}ms (expected < 2000ms)`);

    //TODO: Show subtle notification to user that says slow network connection unexpected network connection
  }

  public getTravelBuddyConversationByUser(
    hubName: string,
    userId: string,
    from: string | Date,
  ): Observable<
    [
      {
        conversation: { ai; user };
      },
    ]
  > {
    return this.apiService.get(
      `visitors/${hubName}/travel-buddy-conversation-by-user?&userId=${userId}&from=${from}`,
    );
  }

  public sendAIQuery(
    hubName: string,
    feature: string,
    model: AIModel,
    query: string,
    history: Array<{ type: string; msg: string }>,
    context?: string,
    options?: { wordCount?: number },
  ): Observable<any> {
    return this.apiService.post(
      `visitors/${hubName}/ai-assistant`,
      {
        feature,
        query,
        model,
        history,
        context,
        // pass through optional tuning parameters
        wordCount: options?.wordCount,
      },
      { timeout: 100 * 1000 },
    );
  }

  public sendAIQueryStream(
    hubName: string,
    feature: string,
    provider: 'openAI',
    query: string,
    history: Array<{ type: string; msg: string }>,
    callback: (data: string) => void,
  ) {
    // return this.apiService.get(`visitors/${hubName}/ai-assistant`, {
    //   feature,
    //   query,
    //   provider,
    //   history,
    // });

    const es = new EventSource(`http://localhost:3000/api/visitors/${hubName}/ai-assistant`);
    es.onmessage = (e) => {
      const data = JSON.parse(e.data);

      if (data.choices[0].finish_reason === 'stop') {
        return es.close();
      }

      const { content = '' } = data.choices[0].delta;

      console.log(content);
      callback && callback(content);
    };
  }

  public getUserTrips(hubName: string, userFacebookId: string): Promise<TripStorageCommand> {
    return lastValueFrom(
      this.apiService.get(`visitors/${hubName}/trips/user/${userFacebookId}`).pipe(
        map((data: TripStorageCommand) => {
          if (!isEmptyArray(data.trips)) {
            data.trips.forEach((trip) => {
              let array = [];
              trip.attractions &&
                trip.attractions.forEach((x) => {
                  if (x.status.toLowerCase() === 'ok') {
                    if (x.feedType === 'event') {
                      this.timeService.processEventTimes(x.item as VisitorEvent);
                    }
                    array.push(x);
                  }
                });
              trip.attractions = array;
            });
          }
          return data;
        }),
      ),
    );
  }

  public getUserTripsV3(
    hubName: string,
    userFacebookId: string,
    source?: string,
  ): Promise<TripStorageCommand> {
    return lastValueFrom(
      this.apiService.get(`visitors/${hubName}/trips/user-v3/${userFacebookId}/${source}`),
    );
  }

  getUserSingleTripsV3(
    hubName: string,
    entireTripId: string,
    singleTripId: string,
  ): Promise<UserTrip> {
    return lastValueFrom(
      this.apiService
        .get(`visitors/${hubName}/trips/single-v3/${entireTripId}/${singleTripId}`)
        .pipe(
          map((data: UserTrip) => {
            if (!isEmptyArray(data.days)) {
              data.days.forEach((day) => {
                let array = [];
                day.attractions &&
                  day.attractions.forEach((x) => {
                    if (x.status.toLowerCase() === 'ok') {
                      if (x.feedType === 'event') {
                        this.timeService.processEventTimes(x.item as VisitorEvent);
                      } else {
                        x.item.address = {
                          address: x.item.address.toString(),
                          lat: x.item.lat,
                          lng: x.item.lng,
                        };
                        (x.item as VisitorPlace).categoryText = this.getDisplayCategory(
                          x.item as VisitorPlace,
                        );
                      }

                      array.push(x);
                    }
                  });
                day.attractions = array;
              });
            }
            return data;
          }),
        ),
    );
  }

  getDisplayCategory(place: VisitorPlace): string {
    if (place?.categoryList || place?.category) {
      return (place.categoryList || place.category.split(', '))
        .map((x) => {
          return this.toReadableCategory(x);
        })
        .join(', ')
        .replace(', Point of Interest', '')
        .replace(', Point Of Interest', '')
        .replace(', Establishment', '');
    }

    return '';
  }

  /**
   * tourist_attraction  => Tourist Attraction
   * b&b => B&B
   * arts_&_culture => Arts & Culture
   * */
  toReadableCategory(category: string) {
    return category
      .trim()
      .replaceAll('&', '_&_')
      .split('_')
      .map((x) => {
        return upperFirstLetter(x);
      })
      .join(' ')
      .replaceAll(' & ', '&');
  }

  public addAttractionToTrip(
    hubName: string,
    command: {
      userFacebookId: string;
      tripId: string;
      attraction: TripAttraction;
    },
  ) {
    return this.apiService.put(`visitors/${hubName}/trips/add-attraction-v3`, command);
  }

  public saveTrip(hubName: string, command: UserTrip, socialId: string) {
    return this.apiService.put(`visitors/${hubName}/trips/single-trip-v3`, {
      command,
      socialId,
    });
  }

  public createTrip(hubName: string, command: CreateTripCommand) {
    return this.apiService.post(`visitors/${hubName}/trips`, command);
  }

  public createTripV3(hubName: string, command: { singleTrip: UserTrip; userFacebookId: string }) {
    return this.apiService.post(`visitors/${hubName}/trips/v3`, command);
  }

  public deleteTrip(hubName: string, userFacebookId: string, id: string) {
    return this.apiService.post(`visitors/${hubName}/trips/delete-trip`, {
      userFacebookId,
      id,
    });
  }

  public removeAttractionFromTrip(
    hubName: string,
    command: {
      feedId: string;
      userFacebookId: string;
      tripIndex: number;
    },
  ) {
    return this.apiService.put(`visitors/${hubName}/trips/remove-attraction`, command);
  }

  public removeAttractionFromTripV3(
    hubName: string,
    command: {
      feedId: string;
      userFacebookId: string;
      tripIndex: number;
    },
  ) {
    return this.apiService.put(`visitors/${hubName}/trips/remove-attraction-v3`, command);
  }

  public ipTest(): Observable<any> {
    return this.apiService.get(`common/ipTest`);
  }

  getCityName(place: VisitorPlace | VisitorEvent) {
    const addressComponents = place.address.addressComponents || place['address_components'];
    if (addressComponents) {
      const level1 = addressComponents.find((x) =>
        x.types.any((y) => y === 'administrative_area_level_1'),
      );
      const locality = addressComponents.find((x) => x.types.any((y) => y === 'locality'));
      const localityName = locality?.long_name ?? locality?.longText ?? '';
      const level1Name = level1?.long_name ?? level1?.longText ?? '';
      return level1 && locality ? `${localityName}, ${level1Name}` : '';
    }
    return '';
  }

  copyToClip(content: string) {
    let aux = document.createElement('input');
    aux.setAttribute('value', content);
    document.body.appendChild(aux);
    aux.select();
    document.execCommand('copy');
    document.body.removeChild(aux);
    // alert('Link is copied to the clipboard');
  }
  public convertToUrlSafeString(inputString: string): string {
    // 1. Remove diacritics (accents and special characters)
    const stringWithoutDiacritics = inputString.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    // 2. Replace spaces with hyphens
    const stringWithHyphens = stringWithoutDiacritics.replace(/\s+/g, '-');
    // 3. Keep only alphanumeric characters, hyphens, and underscores
    const urlSafeString = stringWithHyphens.replace(/[^a-z0-9_-]/gi, '');
    // 4. Convert to lowercase (optional, but recommended for consistency)
    return urlSafeString.toLowerCase();
  }

  getInquiryEmail(hubName: string): Observable<any> {
    return this.apiService.get(`visitors/${hubName}/inquiry-email`);
  }

  sendHelp(help): Observable<any> {
    return this.apiService.post(`common/send-help`, help);
  }

  sendSocketMsg(msgType: string, message: any, tag?: string): Observable<any> {
    return this.apiService.post(`common/send-socket-msg`, { msgType, message, tag });
  }

  // SSE method for TravelBuddy conversation streaming
  travelBuddyConversationStreamSSE(params: {
    hubName: string;
    question: string;
    assistantId?: string;
    threadId?: string;
    userId: string;
    isAnonymous?: boolean;
    userImg?: string;
    userEmail?: string;
    socialName?: string;
    userName?: string;
    cityName?: string;
    isAllowTrackingChat?: boolean;
    context?: any[];
    isCheckPlaceStatusInAdvance?: boolean;
    from?: string;
    _v?: string;
  }) {
    const qs = new URLSearchParams({
      question: params.question,
      // assistantId: params.assistantId || '',
      // threadId: params.threadId || '',
      userId: params.userId,
      isAnonymous: String(!!params.isAnonymous),
      // userImg: params.userImg || '',
      userEmail: params.userEmail || '',
      socialName: params.socialName || '',
      userName: params.userName || '',
      cityName: params.cityName || '',
      // Server reads this only if provided
      // isAllowTrackingChat:
      // params.isAllowTrackingChat === undefined ? '' : String(!!params.isAllowTrackingChat),
      from: params.from || 'travel-buddy',
      context: JSON.stringify(params.context ?? []),
      isCheckPlaceStatusInAdvance: params.isCheckPlaceStatusInAdvance ? 'true' : 'false',
      _v: params._v ?? '',
    });

    // IMPORTANT: hubName is part of the path
    // Get the base URL from the ApiService's environment configuration
    const baseUrl = this.apiService['apiAddress'] || 'http://localhost:3000/api';
    const url = `${baseUrl}visitors/${encodeURIComponent(
      params.hubName,
    )}/travel-buddy-conversation/stream?${qs.toString()}`;

    return new Observable<{
      type: 'message' | 'complete' | 'error';
      content?: string;
      fullResponse?: string;
      data?: any;
    }>((observer) => {
      const es = new EventSource(url, { withCredentials: true });

      const safeParse = (val: any) => {
        try {
          return typeof val === 'string' ? JSON.parse(val) : val;
        } catch {
          return null;
        }
      };

      es.addEventListener('message', (evt: MessageEvent) => {
        const payload = safeParse(evt.data);
        observer.next({
          type: 'message',
          content: payload?.content,
          fullResponse: payload?.fullResponse,
        });
      });

      es.addEventListener('complete', (evt: MessageEvent) => {
        const payload = safeParse(evt.data);
        observer.next({ type: 'complete', data: payload });
        observer.complete();
        es.close();
      });

      es.addEventListener('error', (evt: MessageEvent) => {
        const payload = safeParse((evt as any)?.data);
        observer.next({ type: 'error', data: payload });
        observer.error(payload || new Error('SSE error'));
        es.close();
      });

      // initial comment from server keeps connection open, no handler needed

      return () => {
        try {
          es.close();
        } catch {}
      };
    });
  }

  // Chat Management Methods for Logged-in Users

  /**
   * Save a new chat session for the logged-in user
   */
  // public saveUserChat(
  //   hubName: string,
  //   userId: string,
  //   chatData: { title: string; messages: any[]; isSaved?: boolean }
  // ): Observable<any> {
  //   return this.apiService.post(`visitors/${hubName}/users/${userId}/chats`, chatData);
  // }

  // /**
  //  * Get all chat sessions for a user
  //  */
  // public getUserChats(
  //   hubName: string,
  //   userId: string,
  //   limit?: number,
  //   offset?: number
  // ): Observable<any[]> {
  //   const params: any = {};
  //   if (limit) params.limit = limit;
  //   if (offset) params.offset = offset;

  //   return this.apiService.get(`visitors/${hubName}/users/${userId}/chats`, params);
  // }

  // /**
  //  * Get a specific chat session by ID
  //  */
  // public getUserChatById(hubName: string, userId: string, chatId: string): Observable<any> {
  //   return this.apiService.get(`visitors/${hubName}/users/${userId}/chats/${chatId}`);
  // }

  // /**
  //  * Update an existing chat session
  //  */
  // public updateUserChat(
  //   hubName: string,
  //   userId: string,
  //   chatId: string,
  //   updateData: { title?: string; messages?: any[]; isSaved?: boolean }
  // ): Observable<any> {
  //   return this.apiService.put(`visitors/${hubName}/users/${userId}/chats/${chatId}`, updateData);
  // }

  // /**
  //  * Delete a chat session
  //  */
  // public deleteUserChat(hubName: string, userId: string, chatId: string): Observable<void> {
  //   return this.apiService.delete(`visitors/${hubName}/users/${userId}/chats/${chatId}`);
  // }

  // /**
  //  * Get recent chat sessions (last 10)
  //  */
  // public getUserRecentChats(hubName: string, userId: string): Observable<any[]> {
  //   return this.apiService.get(`visitors/${hubName}/users/${userId}/chats/recent`);
  // }

  // /**
  //  * Save recent chats (for quick access)
  //  */
  // public saveUserRecentChats(hubName: string, userId: string, chatIds: string[]): Observable<void> {
  //   return this.apiService.post(`visitors/${hubName}/users/${userId}/chats/recent`, { chatIds });
  // }

  // /**
  //  * Get saved chat sessions (bookmarked chats)
  //  */
  // public getUserSavedChats(hubName: string, userId: string): Observable<any[]> {
  //   return this.apiService.get(`visitors/${hubName}/users/${userId}/chats/saved`);
  // }

  // /**
  //  * Mark a chat as saved/unsaved
  //  */
  // public toggleUserChatSaved(hubName: string, userId: string, chatId: string, isSaved: boolean): Observable<any> {
  //   return this.apiService.put(`visitors/${hubName}/users/${userId}/chats/${chatId}/saved`, { isSaved });
  // }
}
